import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useLazyLoading from "../../hooks/useLazyLoading";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "./employeeView.css";
import "../../Pages/0PageStyles.css";

//Components
import Title from "../Other Components/Title";
import SearchBar from "../Other Components/searchBar";
import EmployeeCard from "./employeeCard";
import SortComponent from "../Other Components/sortComponent";
import Filter from "../Other Components/filter";
import { SelectionCategory } from "../Other Components/Category";
import PageLoader from "../Other Components/pageLoader";
import EmployeeAddPopup from "./employeeAddPopup";
import ExportData from "../Other Components/ExportDataComponent";

//Functions and hooks
import {
  GetEmployees,
  CheckForEmployee,
} from "../../BackendAccess/EmployeeHooks";
import { GetWarehouseMap } from "../../BackendAccess/WarehouseHooks";
import { GetRoleMap } from "../../BackendAccess/RoleHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import Button from "../Other Components/button";
import { SendEmployeeRequest } from "../../BackendAccess/MessageHooks";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";

export default function EmployeeView({
  Tit = "Employees",
  bEnableSort = true,
  bEnableFilter = true,
  bEnableSearch = true,
  bEnableEdit = true,
  bShowTitle = true,
  bDisabled = false,
  disabledMsg = "Select a warehouse and client first.",
}) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);
  const isNarrow = useNarrowMode();

  const [categoryArray, setCategoryArray] = useState([]);
  const [roleMap, setRoleMap] = useState(null);
  const [warehouseMap, setWarehouseMap] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilteredEmployees, setSearchFilteredEmployees] = useState([]);

  const { t } = useTranslation();
  const { showPopup, removePopup } = usePopup();

  const ShowAddPopup = () => {
    const key = Date.now();
    showPopup(
      <EmployeeAddPopup
        onSend={(body, UserIdentifier) => {
          handleAddEmployee(body, UserIdentifier);
          removePopup(key);
        }}
        onCancel={() => handleCancelAddClick(key)}
      />,
      key
    );
  };

  //Variables for selection and countable modes

  //stuff for lazy loading
  const lazyLoadEmployeeLoadCount = 20;
  const lazyLoadPixelThreshold = 200;

  ////////////////////////////////////Effects/////////////////////////////////////////////

  const fetchDataFromServer = async () => {
    setLoading(true);

    try {
      await fetchEmployees();
      await fetchWarehouseMap();
      await fetchRoleMap();
    } catch (error) {
      error.isPageError = true;
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch employees from the server

  const fetchEmployees = async () => {
    const [data, error] = await fetchWithTimeout(GetEmployees);

    if (error) {
      throw error;
    }
    setEmployees(data);
  };

  const fetchWarehouseMap = async () => {
    const [data, error] = await fetchWithTimeout(GetWarehouseMap);

    if (error) {
      throw error;
    }
    setWarehouseMap(data);
  };

  const fetchRoleMap = async () => {
    const [data, error] = await fetchWithTimeout(GetRoleMap);

    if (error) {
      throw error;
    }
    setRoleMap(data);
  };

  useEffect(() => {
    fetchDataFromServer();
  }, []);

  useEffect(() => {
    if (employees.length > 0 && roleMap !== null && warehouseMap !== null) {
      createCategoryArray();
    }
  }, [employees, roleMap, warehouseMap]);

  useEffect(() => {
    if (employees.length > 0) setFilteredEmployees(employees);
  }, [employees]);

  ////////////////////////////////////Effects end/////////////////////////////////////////////

  ////////////////////////////////////Filtering////////////////////////////////////////////////

  function getKeyByValue(map, value) {
    const entry = Array.from(map.entries()).find(([key, val]) => val === value);
    return entry ? entry[0] : null;
  }

  const createCategoryArray = () => {
    const newCatArr = [
      new SelectionCategory(
        "Role",
        employees.map((employee) => {
          return { ItemName: roleMap.get(employee.RoleID), selected: false };
        }),
        false,
        EmployeeHasOptionRole
      ),
      new SelectionCategory(
        "Warehouse access",
        Array.from(warehouseMap.values()).map((warehouse) => {
          return { ItemName: warehouse, selected: false };
        }),
        false,
        EmployeeHasOptionWarehouse
      ),
    ];
    setCategoryArray(newCatArr);
  };

  const EmployeeHasOptionRole = (employee, role) => {
    return roleMap.get(employee.RoleID) === role;
  };
  const EmployeeHasOptionWarehouse = (employee, warehouse) => {
    return employee.Warehouses.includes(getKeyByValue(warehouseMap, warehouse));
  };

  const setNewCategoryArray = (newCategoryArray) => {
    setCategoryArray(newCategoryArray);
  };

  const setNewEmployees = (newEmployees) => {
    setFilteredEmployees(newEmployees);
  };
  ////////////////////////////////////Filtering end////////////////////////////////////////////////

  /////////////////////////////////SEARCHING AND SORTING/////////////////////////////////////////
  // Handle search input
  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  // Filter employees based on search term
  useEffect(() => {
    const newSearchFilteredEmployees = filteredEmployees.filter((employee) => {
      const lowerCaseTerm = searchTerm.toLowerCase();
      return (
        employee.FirstName.toLowerCase().includes(lowerCaseTerm) ||
        employee.LastName.toLowerCase().includes(lowerCaseTerm) ||
        employee.Email.toLowerCase().includes(lowerCaseTerm)
      );
    });
    setSearchFilteredEmployees(newSearchFilteredEmployees);
  }, [searchTerm, filteredEmployees]);

  const nameSortFunction = (direction) => {
    var employeesSorted = [...filteredEmployees].sort((a, b) =>
      (a.FirstName + " " + a.LastName).toUpperCase() >
      (b.FirstName + " " + b.LastName).toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setEmployees(employeesSorted);
  };
  const idSortFunction = (direction) => {
    var employeesSorted = [...filteredEmployees].sort((a, b) =>
      a.UserID.toUpperCase() > b.UserID.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setEmployees(employeesSorted);
  };

  ////////////////////////////////////////SEARCHING AND SORTING END//////////////////////////////////////

  ////////////////////////////////////////////////Lazy loading///////////////////////////////////////
  const { loadedItems, itemContainerRef } = useLazyLoading(
    searchFilteredEmployees,
    lazyLoadEmployeeLoadCount,
    lazyLoadPixelThreshold
  );

  ////////////////////////////////////////////////Lazy loading end///////////////////////////////////////

  /////////////////////////////////////////Handle btn clicks////////////////////////////////////////

  const handleEditClick = async (id) => {
    const bEmployeeExists = await CheckForEmployee(id);
    if (bEmployeeExists) {
      navigate("/workspace/employees/employeeViewPage", {
        state: { id },
      });
    }
  };
  const handleAddClick = () => {
    ShowAddPopup();
  };
  const handleCancelAddClick = (key) => {
    removePopup(key);
  };
  const handleAddEmployee = async (Body, UserIdentifier) => {
    try {
      const [data, error] = await SendEmployeeRequest(Body, UserIdentifier);
      if (!error) {
        showNotification(
          t("Sent employee request!", { ns: "employees" }),
          "success"
        );
      } else {
        showNotification(
          t("There was an error while sending the request.", {
            ns: "employees",
          }),
          "error"
        );
      }
    } catch (err) {
      showNotification(t("server.Server error.", { ns: "error" }));
    } finally {
    }
  };

  const handleAnnouncementClick = async () => {
    navigate("/workspace/messages/announcementPage");
  };
  /////////////////////////////////////////Handle btn clicks end////////////////////////////////////////

  return (
    <PageLoader loading={loading} error={pageError}>
      <div className="item-view-container">
        {bDisabled ? (
          <div className="itemView disabledCover">
            <p className="itemView disabledMsg">{disabledMsg}</p>
          </div>
        ) : null}
        {bShowTitle ? <Title>{Tit}</Title> : null}
        <div className="btn-container employee-view">
          <Button
            icon="create"
            btnStyle={"btn-positive"}
            onClick={handleAddClick}
          >
            {t("Add employee")}
          </Button>
          <Button
            icon="announcement"
            className={isNarrow ? "mt-2" : "ml-3"}
            btnStyle={"btn-positive"}
            onClick={handleAnnouncementClick}
          >
            {t("Send announcement")}
          </Button>
        </div>
        {bEnableSearch ? (
          <div className="search-bar-container">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("Search employees...", { ns: "employees" })}
            />
          </div>
        ) : null}
        <div className="sortAndFilterBar">
          {bEnableSort ? (
            <SortComponent
              sortOpts={["Name", "ID"]}
              sortOptFunctions={[nameSortFunction, idSortFunction]}
            />
          ) : null}
          {bEnableFilter ? (
            <Filter
              categoryArray={categoryArray}
              ConstItems={employees}
              Employees={filteredEmployees}
              setNewItems={setNewEmployees}
              setNewCatArr={setNewCategoryArray}
            />
          ) : null}
        </div>

        <div className="item-card-and-header-container">
          <div className="item-card-container" ref={itemContainerRef}>
            {loadedItems
              ? loadedItems.map((employee, index) => (
                  <EmployeeCard
                    key={index}
                    UserData={{
                      ...employee,
                      Role: roleMap?.get(employee.RoleID),
                      Name: employee.FirstName + employee.LastName,
                      Warehouses: employee.Warehouses.map((item) => {
                        return warehouseMap?.get(item);
                      }),
                    }}
                    onClick={() => {
                      handleEditClick(employee.UserID);
                    }}
                    bDisabled={loading}
                  />
                ))
              : null}
          </div>
        </div>
        {<ExportData data={searchFilteredEmployees} />}
      </div>
    </PageLoader>
  );
}
